import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Trash2, Search, X, Check, FolderOpen, ImageIcon, Tag, Pencil, Copy, Link2 } from 'lucide-react';
import type { MediaFile } from '../../types/cms';
import { smartCompressImage, formatSize } from '../../lib/imageCompression';
import { applyDiagonalWatermark } from '../../lib/imageWatermark';

interface MediaLibraryProps {
  apiBase: string;
  token: string;
  media: MediaFile[];
  usage: number;
  fetchMedia: () => void;
  showMessage: (msg: string, type?: 'success' | 'error') => void;
  watermarkOpacity?: number;
}

export const MediaLibrary: React.FC<MediaLibraryProps> = ({
  apiBase,
  token,
  media,
  usage,
  fetchMedia,
  showMessage,
  watermarkOpacity,
}) => {
  const ITEMS_PER_PAGE = 20;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [keyword, setKeyword] = useState('');
  const [yearMonth, setYearMonth] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const yearMonths = useMemo(() => {
    const set = new Set<string>();
    media.forEach((m) => {
      const d = new Date(m.createdAt);
      if (!isNaN(d.getTime())) {
        set.add(`${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(set).sort().reverse();
  }, [media]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    media.forEach((m) => {
      if (m.category) set.add(m.category);
    });
    return Array.from(set).sort();
  }, [media]);

  const filteredMedia = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return media.filter((m) => {
      const matchesKw = !kw || m.name.toLowerCase().includes(kw);
      const matchesYm =
        yearMonth === 'all' ||
        (() => {
          const d = new Date(m.createdAt);
          return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}` === yearMonth;
        })();
      const matchesCat =
        categoryFilter === 'all' ||
        (categoryFilter === 'none' ? !m.category : m.category === categoryFilter);
      return matchesKw && matchesYm && matchesCat;
    });
  }, [media, keyword, yearMonth, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMedia.length / ITEMS_PER_PAGE));
  const paginatedMedia = useMemo(
    () => filteredMedia.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredMedia, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [keyword, yearMonth, categoryFilter]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [keyword, yearMonth, categoryFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = new Set(filteredMedia.map((m) => m.id));
    const allSelected = filteredMedia.every((m) => selectedIds.has(m.id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...visibleIds]));
    }
  };

  const handleUnauthorized = useCallback(async () => {
    showMessage('登入已過期，請重新登入', 'error');
    localStorage.removeItem('marveland_admin_token');
    localStorage.removeItem('marveland_admin_role');
    localStorage.removeItem('marveland_admin_username');
    localStorage.removeItem('marveland_admin_userid');
    window.location.reload();
  }, [showMessage]);

  const uploadFile = async (file: File): Promise<{ id?: string; compressed: boolean; ratio: string; originalSize: number; compressedSize: number } | undefined> => {
    let compressed;
    try {
      // 防盜水印（中央對角 MARVELAND）先於壓縮套用——濃度由後台「防盜水印濃度」設定
      const marked = await applyDiagonalWatermark(file, watermarkOpacity);
      compressed = await smartCompressImage(marked);
    } catch (err) {
      showMessage(`圖片處理失敗：${err instanceof Error ? err.message : String(err)}`, 'error');
      throw err;
    }

    const formData = new FormData();
    formData.append('file', compressed.file);
    const res = await fetch(`${apiBase}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (res.status === 401) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      let errorText = '';
      try {
        const data = await res.json();
        errorText = data.error || `HTTP ${res.status}`;
      } catch {
        errorText = `HTTP ${res.status}`;
      }
      showMessage(`上傳失敗：${errorText}`, 'error');
      throw new Error(errorText);
    }

    const data = await res.json();
    return {
      id: data?.media?.id,
      compressed: compressed.isCompressed,
      ratio: compressed.ratio,
      originalSize: compressed.originalSize,
      compressedSize: compressed.compressedSize,
    };
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setUploadProgress(`0 / ${files.length}`);
    let success = 0;
    let failed = 0;
    let compressedCount = 0;
    let savedBytes = 0;

    for (let i = 0; i < files.length; i++) {
      setUploadProgress(`${i + 1} / ${files.length}`);
      try {
        const result = await uploadFile(files[i]);
        success++;
        if (result?.compressed) {
          compressedCount++;
          savedBytes += result.originalSize - result.compressedSize;
        }
      } catch {
        failed++;
      }
    }

    await fetchMedia();
    setIsUploading(false);
    setUploadProgress('');
    if (failed === 0) {
      const savedText = savedBytes > 0 ? `，節省 ${formatSize(savedBytes)}` : '';
      showMessage(`成功上傳 ${success} 張圖片（${compressedCount} 張壓縮${savedText}）`);
    } else {
      showMessage(`上傳完成：${success} 成功，${failed} 失敗`, 'error');
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const deleteMedia = async (id: string) => {
    const res = await fetch(`${apiBase}/api/media/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      await handleUnauthorized();
      return;
    }
    if (!res.ok) {
      let errorText = '';
      try {
        const data = await res.json();
        errorText = data.error || `HTTP ${res.status}`;
      } catch {
        errorText = `HTTP ${res.status}`;
      }
      showMessage(`刪除失敗：${errorText}`, 'error');
      return;
    }
    showMessage('已刪除');
    fetchMedia();
  };

  const copyUrl = async (id: string) => {
    const url = `${apiBase}/api/media/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      showMessage('已複製圖片 URL');
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch {
      showMessage('複製失敗，請手動複製：' + url, 'error');
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`確定要刪除選取的 ${selectedIds.size} 項媒體嗎？此操作無法復原。`)) return;
    let failed = 0;
    for (const id of selectedIds) {
      const res = await fetch(`${apiBase}/api/media/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) failed++;
    }
    setSelectedIds(new Set());
    await fetchMedia();
    if (failed) {
      showMessage(`刪除完成，${failed} 項失敗`, 'error');
    } else {
      showMessage('已刪除選取項目');
    }
  };

  const updateMedia = async (id: string, updates: Partial<MediaFile>) => {
    const res = await fetch(`${apiBase}/api/media/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(updates),
    });
    if (res.status === 401) {
      await handleUnauthorized();
      return false;
    }
    if (!res.ok) {
      let errorText = '';
      try {
        const data = await res.json();
        errorText = data.error || `HTTP ${res.status}`;
      } catch {
        errorText = `HTTP ${res.status}`;
      }
      showMessage(`更新失敗：${errorText}`, 'error');
      return false;
    }
    return true;
  };

  const saveRename = async (id: string) => {
    const tags = editTags
      .split(/[,，]/)
      .map((t) => t.trim())
      .filter(Boolean);
    const success = await updateMedia(id, { name: editName, category: editCategory, tags });
    if (success) {
      setEditingId(null);
      showMessage('已更新');
      fetchMedia();
    }
  };

  const startEdit = (m: MediaFile) => {
    setEditingId(m.id);
    setEditName(m.name);
    setEditCategory(m.category || '');
    setEditTags(m.tags?.join(', ') || '');
  };

  const applyBulkCategory = async () => {
    const cat = newCategory.trim() || bulkCategory;
    if (!cat || selectedIds.size === 0) return;
    let failed = 0;
    for (const id of selectedIds) {
      const ok = await updateMedia(id, { category: cat });
      if (!ok) failed++;
    }
    setSelectedIds(new Set());
    setNewCategory('');
    await fetchMedia();
    if (failed) {
      showMessage(`分類更新完成，${failed} 項失敗`, 'error');
    } else {
      showMessage('已更新分類');
    }
  };

  return (
    <div
      className={`space-y-4 ${isDragging ? 'bg-blue-50' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <p className="font-bold">已用容量</p>
          <p className="text-sm text-slate-500">{(usage / 1024 / 1024).toFixed(2)} MB / 1 GB</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={isUploading}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900 disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            {isUploading ? `上傳中 ${uploadProgress}` : '上傳圖片'}
          </button>
        </div>
      </div>

      {isDragging && (
        <div className="rounded-xl border-2 border-dashed border-blue-400 bg-blue-50 p-8 text-center text-blue-700">
          <FolderOpen className="mx-auto mb-2 h-8 w-8" />
          <p className="font-bold">放開即可上傳圖片</p>
        </div>
      )}

      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500">年月</label>
            <select
              value={yearMonth}
              onChange={(e) => setYearMonth(e.target.value)}
              className="rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
            >
              <option value="all">全部</option>
              {yearMonths.map((ym) => (
                <option key={ym} value={ym}>
                  {ym}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500">分類</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
            >
              <option value="all">全部分類</option>
              <option value="none">未分類</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500">關鍵字</label>
            <div className="flex items-center rounded border border-slate-300 px-2">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="檔案名稱"
                className="px-2 py-2 text-sm outline-none"
              />
            </div>
          </div>
          <div className="flex-1" />
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-600">已選 {selectedIds.size} 項</span>
              <select
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                className="rounded border border-slate-300 px-2 py-2 text-sm outline-none focus:border-blue-800"
              >
                <option value="">選擇分類</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="或新增分類"
                className="w-28 rounded border border-slate-300 px-2 py-2 text-sm outline-none focus:border-blue-800"
              />
              <button
                onClick={applyBulkCategory}
                disabled={(!bulkCategory && !newCategory.trim()) || selectedIds.size === 0}
                className="flex items-center gap-1 rounded bg-blue-800 px-3 py-2 text-sm font-bold text-white hover:bg-blue-900 disabled:opacity-60"
              >
                <Tag className="h-4 w-4" />
                設分類
              </button>
              <button
                onClick={bulkDelete}
                className="flex items-center gap-1 rounded bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                刪除
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
          <button
            onClick={toggleSelectAll}
            className="flex aspect-square flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
          >
            <input
              type="checkbox"
              checked={filteredMedia.length > 0 && filteredMedia.every((m) => selectedIds.has(m.id))}
              readOnly
              className="mb-2 h-5 w-5"
            />
            <span className="text-xs font-bold">全選</span>
          </button>

          {paginatedMedia.map((file) => {
            const isSelected = selectedIds.has(file.id);
            const isEditing = editingId === file.id;
            return (
              <div
                key={file.id}
                className={`relative overflow-hidden rounded-lg shadow-sm ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className="aspect-square bg-slate-100">
                  {file.mimeType.startsWith('image/') ? (
                    <img
                      src={file.data || `${apiBase}/api/media/${file.id}`}
                      alt={file.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="absolute left-2 top-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(file.id)}
                    className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="p-2">
                  {isEditing ? (
                    <div className="space-y-1">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename(file.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs outline-none focus:border-blue-800"
                        autoFocus
                      />
                      <input
                        type="text"
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        placeholder="分類"
                        className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs outline-none focus:border-blue-800"
                      />
                      <input
                        type="text"
                        value={editTags}
                        onChange={(e) => setEditTags(e.target.value)}
                        placeholder="標籤（用逗號分隔）"
                        className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs outline-none focus:border-blue-800"
                      />
                      <div className="flex gap-1">
                        <button onClick={() => saveRename(file.id)} className="rounded bg-green-600 p-0.5 text-white">
                          <Check className="h-3 w-3" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="rounded bg-slate-300 p-0.5 text-slate-700">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-1">
                        <button
                          onClick={() => startEdit(file)}
                          className="block flex-1 truncate text-left text-xs hover:text-blue-800"
                          title={file.name}
                        >
                          {file.name}
                        </button>
                        <button
                          onClick={() => startEdit(file)}
                          title="編輯名稱、分類與標籤"
                          className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-blue-800"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                      {file.category && (
                        <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                          分類：{file.category}
                        </span>
                      )}
                      {file.tags && file.tags.filter((tag) => tag !== file.category).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {file.tags
                            .filter((tag) => tag !== file.category)
                            .map((tag) => (
                              <span key={tag} className="inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">
                                {tag}
                              </span>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                  <p className="text-[10px] text-slate-500">{formatSize(file.size)}</p>
                  {!isEditing && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => copyUrl(file.id)}
                        className="flex items-center gap-1 text-xs text-blue-700 hover:underline"
                        title="複製圖片 URL"
                      >
                        {copiedId === file.id ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Link2 className="h-3 w-3" />
                        )}
                        {copiedId === file.id ? '已複製' : '複製 URL'}
                      </button>
                      <button
                        onClick={() => deleteMedia(file.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        刪除
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredMedia.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-sm text-slate-500">
              顯示第 <span className="font-bold text-slate-900">{Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filteredMedia.length)}</span> 至 {' '}
              <span className="font-bold text-slate-900">{Math.min(currentPage * ITEMS_PER_PAGE, filteredMedia.length)}</span> 項，
              共 <span className="font-bold text-slate-900">{filteredMedia.length}</span> 項
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                上一頁
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`rounded px-3 py-1.5 text-sm font-bold ${
                    page === currentPage
                      ? 'bg-blue-800 text-white'
                      : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
          </div>
        )}

        {filteredMedia.length === 0 && (
          <div className="py-12 text-center text-slate-500">沒有符合條件的媒體</div>
        )}
      </div>
    </div>
  );
};
